import {useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import {alpha} from "@mui/material/styles";
import {type GridColDef} from "@mui/x-data-grid";
import {useTranslation} from "react-i18next";
import {useAdminLegalEntities, useAdminUsers, useCreateUser, useSoftDeleteUser} from "../../api/admin/adminQueries";
import ArenaSnackBar from "../ArenaSnackBar";
import AdminDataGrid from "./AdminDataGrid";
import SectionShell from "./AdminShared";
import {type LegalEntity, type ManagedUser, type UserDraft} from "./AdminTypes";

const emptyLegalEntities: LegalEntity[] = [];
const emptyUsers: ManagedUser[] = [];

const createInitialUserDraft = (legalEntityId: string): UserDraft => ({
    name: "",
    password: "",
    preferredLocale: "en",
    legalEntityId,
});

const UserManagementTab = () => {
    const {t: translate} = useTranslation();
    const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
    const [selectedLegalEntityFilter, setSelectedLegalEntityFilter] = useState("");
    const [userDraft, setUserDraft] = useState<UserDraft>(createInitialUserDraft(""));
    const legalEntitiesQuery = useAdminLegalEntities();
    const createUserMutation = useCreateUser();
    const softDeleteUserMutation = useSoftDeleteUser();
    const legalEntities = legalEntitiesQuery.data ?? emptyLegalEntities;
    const usersQuery = useAdminUsers(selectedLegalEntityFilter || undefined);
    const users = usersQuery.data ?? emptyUsers;

    const legalEntityNamesById = useMemo(
        () => new Map(legalEntities.map((legalEntity) => [legalEntity.id, legalEntity.name])),
        [legalEntities],
    );

    const pageErrorMessage = legalEntitiesQuery.error?.message || usersQuery.error?.message;
    const createUserDialogErrorMessage = createUserMutation.error?.message;
    const softDeleteUserErrorMessage = softDeleteUserMutation.error?.message;

    const userColumns = useMemo<GridColDef<ManagedUser>[]>(
        () => [
            {
                field: "name",
                headerName: translate("admin.shared.name"),
                flex: 1.1,
                minWidth: 180,
            },
            {
                field: "legalEntityId",
                headerName: translate("admin.shared.legalEntity"),
                flex: 1.2,
                minWidth: 220,
                valueGetter: (_value, row) =>
                    legalEntityNamesById.get(row.legalEntityId) ?? translate("admin.shared.unknown"),
            },
            {
                field: "role",
                headerName: translate("admin.userManagement.columns.role"),
                flex: 0.7,
                minWidth: 140,
                renderCell: ({row}) => (
                    <Chip
                        label={translate(`admin.userManagement.roles.${row.role.toLowerCase()}`, {
                            defaultValue: row.role,
                        })}
                        size="small"
                        sx={{fontWeight: 700, letterSpacing: 0.4}}
                    />
                ),
            },
            {
                field: "preferredLocale",
                headerName: translate("admin.userManagement.columns.preferredLocale"),
                flex: 0.7,
                minWidth: 160,
                valueGetter: (_value, row) =>
                    translate(`settings.${row.preferredLocale.toLowerCase()}`, {
                        defaultValue: row.preferredLocale.toUpperCase(),
                    }),
            },
            {
                field: "actions",
                headerName: translate("admin.shared.actions"),
                flex: 0.85,
                minWidth: 150,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                align: "right",
                headerAlign: "right",
                renderCell: ({row}) => (
                    <Tooltip title={translate("admin.userManagement.softDelete.tooltip")}>
                        <IconButton
                            size="small"
                            color="error"
                            aria-label={translate("admin.userManagement.softDelete.ariaLabel", {name: row.name})}
                            disabled={softDeleteUserMutation.isPending}
                            onClick={() =>
                                softDeleteUserMutation.mutate({userId: row.id, legalEntityId: row.legalEntityId})
                            }
                        >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ),
            },
        ],
        [legalEntityNamesById, softDeleteUserMutation, translate],
    );

    const handleOpenCreateUser = () => {
        if (!legalEntities.length) {
            return;
        }

        createUserMutation.reset();
        setUserDraft((previousDraft) => ({
            ...previousDraft,
            legalEntityId: previousDraft.legalEntityId || legalEntities[0]?.id || "",
        }));
        setIsCreateUserOpen(true);
    };

    const handleCloseCreateUser = () => {
        createUserMutation.reset();
        setIsCreateUserOpen(false);
    };

    const handleCreateUser = async () => {
        try {
            await createUserMutation.mutateAsync(userDraft);
            setSelectedLegalEntityFilter(userDraft.legalEntityId);
            setUserDraft(createInitialUserDraft(userDraft.legalEntityId));
            setIsCreateUserOpen(false);
        } catch {
            return;
        }
    };

    return (
        <>
            <SectionShell
                eyebrow={translate("admin.userManagement.eyebrow")}
                title={translate("admin.userManagement.title")}
                action={
                    <Button
                        variant="contained"
                        onClick={handleOpenCreateUser}
                        disabled={!legalEntities.length || legalEntitiesQuery.isLoading}
                        sx={{
                            borderRadius: 999,
                            bgcolor: "primary.main",
                            fontWeight: 700,
                            boxShadow: "none",
                        }}
                    >
                        {translate("admin.userManagement.actions.createUser")}
                    </Button>
                }
            >
                <Stack spacing={3}>
                    <Stack direction={{xs: "column", md: "row"}} spacing={2}>
                        <FormControl fullWidth sx={{maxWidth: 340}} disabled={legalEntitiesQuery.isLoading}>
                            <InputLabel id="legal-entity-filter-label">
                                {translate("admin.shared.legalEntity")}
                            </InputLabel>
                            <Select
                                labelId="legal-entity-filter-label"
                                label={translate("admin.shared.legalEntity")}
                                value={selectedLegalEntityFilter}
                                onChange={(event) => setSelectedLegalEntityFilter(event.target.value)}
                            >
                                {legalEntities.map((legalEntity) => (
                                    <MenuItem key={legalEntity.id} value={legalEntity.id}>
                                        {legalEntity.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Alert
                            severity="info"
                            sx={(theme) => ({
                                // flex: 1,
                                borderRadius: 3,
                                bgcolor: alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === "light" ? 0.08 : 0.16,
                                ),
                                color: theme.palette.text.primary,
                                ".MuiAlert-icon": {color: theme.palette.primary.main},
                                alignItems: "center",
                            })}
                        >
                            {translate("admin.userManagement.selectLegalEntityAlert")}
                        </Alert>
                    </Stack>

                    {pageErrorMessage ? <Alert severity="error">{pageErrorMessage}</Alert> : null}

                    <Box sx={{position: "relative"}}>
                        {!selectedLegalEntityFilter ? (
                            <Paper
                                elevation={0}
                                sx={(theme) => ({
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 3,
                                    bgcolor: alpha(
                                        theme.palette.background.default,
                                        theme.palette.mode === "light" ? 0.88 : 0.94,
                                    ),
                                    border: "1px dashed",
                                    borderColor: alpha(theme.palette.primary.main, 0.28),
                                })}
                            >
                                <Stack spacing={1} alignItems="center">
                                    <ManageAccountsRoundedIcon sx={{fontSize: 34, color: "primary.main"}} />
                                    <Typography sx={{fontWeight: 700, color: "text.primary"}}>
                                        {translate("admin.userManagement.emptyState")}
                                    </Typography>
                                </Stack>
                            </Paper>
                        ) : null}

                        <Box sx={{opacity: selectedLegalEntityFilter ? 1 : 0.35}}>
                            <AdminDataGrid
                                rows={users}
                                columns={userColumns}
                                autoHeight
                                loading={usersQuery.isLoading || usersQuery.isFetching}
                            />
                        </Box>
                    </Box>
                </Stack>
            </SectionShell>

            <Dialog open={isCreateUserOpen} onClose={handleCloseCreateUser} fullWidth maxWidth="sm">
                <DialogTitle>{translate("admin.userManagement.createUserDialog.title")}</DialogTitle>
                <DialogContent sx={{pt: 1}}>
                    <Stack spacing={2} sx={{mt: 1}}>
                        {createUserDialogErrorMessage ? (
                            <Alert severity="error">{createUserDialogErrorMessage}</Alert>
                        ) : null}
                        <TextField
                            label={translate("admin.shared.name")}
                            value={userDraft.name}
                            onChange={(event) =>
                                setUserDraft((previousDraft) => ({...previousDraft, name: event.target.value}))
                            }
                            fullWidth
                        />
                        <TextField
                            label={translate("admin.shared.password")}
                            type="password"
                            value={userDraft.password}
                            onChange={(event) =>
                                setUserDraft((previousDraft) => ({...previousDraft, password: event.target.value}))
                            }
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel id="user-locale-label">
                                {translate("admin.userManagement.columns.preferredLocale")}
                            </InputLabel>
                            <Select
                                labelId="user-locale-label"
                                label={translate("admin.userManagement.columns.preferredLocale")}
                                value={userDraft.preferredLocale}
                                onChange={(event) =>
                                    setUserDraft((previousDraft) => ({
                                        ...previousDraft,
                                        preferredLocale: event.target.value,
                                    }))
                                }
                            >
                                <MenuItem value="en">{translate("settings.en")}</MenuItem>
                                <MenuItem value="de">{translate("settings.de")}</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="user-legal-entity-label">
                                {translate("admin.shared.legalEntity")}
                            </InputLabel>
                            <Select
                                labelId="user-legal-entity-label"
                                label={translate("admin.shared.legalEntity")}
                                value={userDraft.legalEntityId}
                                onChange={(event) =>
                                    setUserDraft((previousDraft) => ({
                                        ...previousDraft,
                                        legalEntityId: event.target.value,
                                    }))
                                }
                            >
                                <MenuItem value="" disabled>
                                    {translate("admin.userManagement.createUserDialog.selectLegalEntity")}
                                </MenuItem>
                                {legalEntities.map((legalEntity) => (
                                    <MenuItem key={legalEntity.id} value={legalEntity.id}>
                                        {legalEntity.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2.5}}>
                    <Button variant="outlined" onClick={handleCloseCreateUser}>
                        {translate("common.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateUser}
                        disabled={createUserMutation.isPending}
                        sx={{bgcolor: "primary.main"}}
                    >
                        {translate("admin.userManagement.actions.createUser")}
                    </Button>
                </DialogActions>
            </Dialog>

            <ArenaSnackBar message={softDeleteUserErrorMessage} onClose={() => softDeleteUserMutation.reset()} />
        </>
    );
};

export default UserManagementTab;
